import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { CalendarIcon, MapPinIcon, UsersIcon } from "@heroicons/react/20/solid";
import { getCasesAsync } from "../../../../slices/case";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { ResourceError } from "../../../utils/ResourceError";
import { toast } from "react-hot-toast";
import { getReportsAsync } from "../../../../slices/service/reports";
import { TWclassNames } from "../../../utils/Div";
import {
  FullReportAddress,
  ReportAppointmentApprovedStatus,
  ReportStatus,
  reportStatuses,
  reportStatusesOptions,
  reportViewsOptions,
} from "./utils";
import {
  DocumentArrowUpIcon,
  PencilIcon,
  WrenchIcon,
  BuildingLibraryIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import {
  CustomerTitle,
  StringLimit,
  dateToString,
  formattedDate,
  removeHtml,
  reportPath,
} from "../../../Utils";
import ReportFinishDialog from "./report-finish-dialog";
import ServiceReportEditModal from "./service-report-edit-modal";
import ReportOverviewSummary from "./ReportOverviewSummary";
import { PMPagination } from "../../../utils/PMPagination";
import { ModuleFilter } from "../../../utils/ModuleFilter";
import { PeriodSelector } from "../../hours/period_selector";
import { ReportsProjectExportDialog } from "./reports-project-export-dialog";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";

export const ReportsStackList = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const itemState = useSelector((store) => {
    return store.reports;
  });

  const { isLoading, data, error, errors, currentCount, count } = itemState;

  const { sort, sortBy, fields, showDeleted, per_page } = props;

  // Period state
  const [period, setPeriod] = useState({
    periodType: "month",
    from: null,
    to: null,
  });

  // Date field filter (agreement_date, order_date, or "all")
  const [dateField, setDateField] = useState("all");

  const [controller, setController] = useState({
    filters: [...props.filters],
    showDeleted: showDeleted,
    page: 1,
    query: "",
    sort: sort.length > 1 ? sort : "desc",
    sortBy: sortBy.length > 1 ? sortBy : "updated_at",
    view: "all",
    per_page: per_page,
    fields: fields,
    date_from: null,
    date_to: null,
    date_field: "all", // "agreement_date", "order_date", or "all"
  });

  // Initialize ModuleFilter state from controller
  const initialFilters = useMemo(() => {
    const initial = {
      query: controller.query || "",
      statuses: [],
      views: [],
    };

    if (controller.filters && controller.filters.length > 0) {
      controller.filters.forEach((filter) => {
        if (filter.name === "status") {
          try {
            const parsedStatuses = JSON.parse(filter.value);
            if (parsedStatuses && parsedStatuses.length > 0) {
              initial.statuses = parsedStatuses;
            }
          } catch (e) {
            console.error("Error parsing status filter:", e);
          }
        } else if (filter.name === "view") {
          try {
            const parsedViews = JSON.parse(filter.value);
            if (parsedViews && parsedViews.length > 0) {
              initial.views = parsedViews;
            }
          } catch (e) {
            console.error("Error parsing view filter:", e);
          }
        }
      });
    }

    return initial;
  }, [controller.query, controller.filters]);

  // Convert ModuleFilter format to backend format
  const handleFiltersChange = (filters) => {
    setController((prevController) => {
      // Start with existing filters to preserve project filter and other filters
      const backendFilters = [...(prevController.filters || props.filters || [])];

      // Map status filters
      if (filters.statuses && filters.statuses.length > 0) {
        const statusFilterIndex = backendFilters.findIndex(f => f.name === "status");
        if (statusFilterIndex >= 0) {
          backendFilters[statusFilterIndex] = {
            name: "status",
            value: JSON.stringify(filters.statuses),
          };
        } else {
          backendFilters.push({
            name: "status",
            value: JSON.stringify(filters.statuses),
          });
        }
      } else {
        // Remove status filter if empty
        const statusFilterIndex = backendFilters.findIndex(f => f.name === "status");
        if (statusFilterIndex >= 0) {
          backendFilters.splice(statusFilterIndex, 1);
        }
      }

      // Map view filters
      if (filters.views && filters.views.length > 0) {
        const viewFilterIndex = backendFilters.findIndex(f => f.name === "view");
        if (viewFilterIndex >= 0) {
          backendFilters[viewFilterIndex] = {
            name: "view",
            value: JSON.stringify(filters.views),
          };
        } else {
          backendFilters.push({
            name: "view",
            value: JSON.stringify(filters.views),
          });
        }
      } else {
        // Remove view filter if empty
        const viewFilterIndex = backendFilters.findIndex(f => f.name === "view");
        if (viewFilterIndex >= 0) {
          backendFilters.splice(viewFilterIndex, 1);
        }
      }

      return {
        ...prevController,
        page: 1,
        query: filters.query || "",
        filters: backendFilters,
      };
    });
  };

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    if (!newPeriod) return;
    
    setPeriod(newPeriod);
    setController((prevController) => ({
      ...prevController,
      page: 1,
      // Only set date filters if both from and to are present
      date_from: newPeriod.from || null,
      date_to: newPeriod.to || null,
      date_field: dateField,
    }));
  };

  // Handle date field change
  const handleDateFieldChange = (newDateField) => {
    setDateField(newDateField);
    setController((prevController) => ({
      ...prevController,
      page: 1,
      date_field: newDateField,
    }));
  };

  const handleQueryChange = (newQuery) => {
    setController({
      ...controller,
      page: 1,
      query: newQuery,
    });
  };

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  useEffect(() => {
    const handleFilterChange = () => {
      setController((prevController) => ({
        ...prevController,
        page: 1,
        filters: [...props.filters],
      }));
    };

    if (props.filters) {
      handleFilterChange();
    }
  }, [props.filters]);

  useEffect(() => {
    dispatch(getReportsAsync(controller));
  }, [dispatch, controller]);

  const [selectedCase, setSelectedCase] = useState(null);

  function handleOpenCase(id) {
    setSelectedCase(id);
  }

  const navigate = useNavigate();

  const [finishedReportDialogOpen, setFinishedReportDialogOpen] =
    useState(false);

  const [editReportDialogOpen, setEditReportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [reportOverviewDialogOpen, setReportOverviewDialogOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  const [selectedReport, setSelectedReport] = useState(null);

  const handleOpenDialog = (type, item) => {
    if (type === "report") {
      setSelectedReport(item);
      setFinishedReportDialogOpen(true);
    }

    if (type === "edit") {
      setSelectedReport(item);
      setEditReportDialogOpen(true);
    }

    if (type === "overview") {
      setSelectedReportId(item.id);
      setReportOverviewDialogOpen(true);
    }
  };

  // Checks if agreement_date is before today. Return false if it is.

  const isAgreementDateBeforeToday = (agreement_date) => {
    const today = new Date();
    const agreementDate = new Date(agreement_date);
    return agreementDate > today;
  };

  // Convert status options to ModuleFilter format
  const statusOptions = reportStatusesOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-blue-100 text-blue-800",
  }));

  // Convert view options to ModuleFilter format
  const viewOptions = reportViewsOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-purple-100 text-purple-800",
  }));

  return (
    <>
      <div className="space-y-4 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <PeriodSelector
              periodType={period.periodType}
              from={period.from}
              to={period.to}
              onChange={handlePeriodChange}
            />
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                {t("Dato-felt")}:
              </label>
              <select
                value={dateField}
                onChange={(e) => handleDateFieldChange(e.target.value)}
                className="px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none cursor-pointer"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                  backgroundPosition: 'right 0.5rem center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="all">{t("Alle")}</option>
                <option value="agreement_date">{t("Avtalt dato")}</option>
                <option value="order_date">{t("Ordredato")}</option>
              </select>
            </div>
          </div>
          <button
            onClick={() => setExportDialogOpen(true)}
            disabled={isLoading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
            {t("Eksporter")}
          </button>
        </div>
        <ModuleFilter
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={initialFilters}
          searchPlaceholder="Søk rapporter..."
          filterTitle="Filtrer rapporter"
          acceptedStatuses={reportStatusesOptions.map((opt) => opt.value)}
          customDropdownFilters={[
            {
              key: "views",
              label: "Visninger",
              options: viewOptions,
              icon: BuildingLibraryIcon,
            },
          ]}
        />
      </div>

      {isLoading ? (
        <div className="mt-10">
          <ResourceLoading />
        </div>
      ) : error ? (
        <ResourceError errors={errors} />
      ) : !data || data.length === 0 ? (
        <ResourceEmpty />
      ) : (
        <div className="overflow-hidden bg-white shadow sm:rounded-lg">
          <ul role="list" className="divide-y divide-gray-200">
            {data?.map((item) => (
              <li key={item?.id} className="flex justify-between items-center">
                <Link
                  className="px-6 py-6 sm:px-8 flex-grow hover:bg-gray-50"
                  to={reportPath({ report: item })}
                >
                  <div className="flex justify-between">
                    <div className="flex flex-col justify-between">
                      <div className="flex items-center">
                        <div className="grid">
                          <div className="mb-2">
                            <ReportStatus report={item} />
                            {isAgreementDateBeforeToday(
                              item?.appointment?.date
                            ) && (
                              <ReportAppointmentApprovedStatus
                                type="short"
                                report={item}
                                className="ml-2"
                              />
                            )}
                          </div>
                          <p className="truncate text-sm font-medium text-blue-600">
                            {item?.displayTitle}
                          </p>
                          <p className="text-xs">{item?.report_type?.title}</p>
                          <p>
                            {item?.description && (
                              <StringLimit
                                limit={30}
                                text={removeHtml(item?.description)}
                              />
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-600">
                            <UsersIcon
                              className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-500"
                              aria-hidden="true"
                            />
                            <CustomerTitle customer={item?.customer} />
                          </p>
                          {item?.delivery_address?.id && (
                            <p className="sm:ml-2 flex items-center text-sm text-gray-600">
                              <MapPinIcon
                                className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-500"
                                aria-hidden="true"
                              />
                              <StringLimit
                                limit={30}
                                text={FullReportAddress(item)}
                              />
                            </p>
                          )}
                          {item?.executing_employee?.id && (
                            <p className="sm:ml-2 flex items-center text-sm text-gray-600">
                              <WrenchIcon
                                className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-500"
                                aria-hidden="true"
                              />
                              {item?.executing_employee?.name}
                            </p>
                          )}
                        </div>
                        <div className="sm:ml-2 flex items-center text-sm text-gray-600">
                          <CalendarIcon
                            className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-500"
                            aria-hidden="true"
                          />
                          <p>
                            Utførelse dato:{" "}
                            <time dateTime={item?.appointment?.date}>
                              {/* {item?.appointment?.date || "Ikke avtalt"} */}
                              {dateToString(item?.appointment?.date)}
                            </time>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
                <div className="flex flex-col h-full">
                  <button
                    onClick={() => handleOpenDialog("edit", item)}
                    className="flex bg-gray-50 px-4 py-6 text-black hover:bg-gray-300"
                  >
                    <PencilIcon
                      className="mt-1 h-5 w-5 mr-2"
                      aria-hidden="true"
                    />
                    <span>{t("Edit")}</span>
                  </button>
                  <button
                    onClick={() => handleOpenDialog("overview", item)}
                    className="flex bg-gray-50 px-4 py-6 text-blue-600 hover:bg-gray-300"
                  >
                    <EyeIcon
                      className="mt-1 h-5 w-5 mr-2"
                      aria-hidden="true"
                    />
                    <span>{t("Overview")}</span>
                  </button>
                  <button
                    disabled={item?.finished !== true}
                    onClick={() => handleOpenDialog("report", item)}
                    className={TWclassNames(
                      item?.finished !== true
                        ? "text-gray-200 cursor-not-allowed"
                        : "text-green-400",
                      "flex bg-gray-50 px-4 py-6  hover:bg-gray-300"
                    )}
                  >
                    <DocumentArrowUpIcon
                      className="mt-1 h-5 w-5 mr-2"
                      aria-hidden="true"
                    />
                    <span>{t("Report")}</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && !error && data && data.length > 0 && (
        <PMPagination
          disabled={isLoading}
          onPageChange={handlePageChange}
          page={controller.page}
          perPage={controller.per_page}
          rowsCount={currentCount}
          totalCount={count}
          currentCount={currentCount}
          showCount={true}
        />
      )}
      <ReportFinishDialog
        open={finishedReportDialogOpen}
        onClose={() => setFinishedReportDialogOpen(false)}
        selected={selectedReport}
        onRefresh={props.onRefresh}
        isLoading={isLoading}
      />

      <ServiceReportEditModal
        open={editReportDialogOpen}
        onClose={() => setEditReportDialogOpen(false)}
        selected={selectedReport}
        redirect={false}
        // onRefresh={handleOnRefresh}
      />

      <ReportsProjectExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        project={props.project}
        controller={controller}
        period={period}
      />

      <ReportOverviewSummary
        open={reportOverviewDialogOpen}
        onClose={() => setReportOverviewDialogOpen(false)}
        reportId={selectedReportId}
        currentUser={null} // Stack list doesn't have current user context
      />
    </>
  );
};

ReportsStackList.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "created_at",
};
